import { useState, useEffect, useCallback, useRef, RefObject } from 'react';

type UseMediaLoadingOptions = {
  cardRef: RefObject<HTMLDivElement>;
  imageUrl?: string;
  isVideo?: boolean;
  poster?: string;
  /**
   * How long a card must stay visible before its video starts loading. Guards
   * against fetching videos the user only scrolls past. Defaults to 2000ms;
   * grids where previews should play promptly (e.g. the dance motion grid) pass
   * a shorter value.
   */
  videoLoadDelay?: number;
};

const DEFAULT_VIDEO_LOAD_DELAY = 2000;

const useMediaLoading = ({
  cardRef,
  imageUrl,
  isVideo,
  poster,
  videoLoadDelay = DEFAULT_VIDEO_LOAD_DELAY,
}: UseMediaLoadingOptions) => {
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const videoLoadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const posterRef = useRef<HTMLImageElement | null>(null);

  const clearVideoLoadTimer = useCallback(() => {
    if (videoLoadTimerRef.current) {
      clearTimeout(videoLoadTimerRef.current);
      videoLoadTimerRef.current = null;
    }
  }, []);

  // Reset states when URL changes
  useEffect(() => {
    setMediaLoaded(false);
    setShouldLoadVideo(false);
    setVideoReady(false);
    clearVideoLoadTimer();
  }, [imageUrl, clearVideoLoadTimer]);

  // Delayed video loading after 2s of visibility
  useEffect(() => {
    if (!isVideo) return;
    const card = cardRef.current;
    if (!card) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!videoLoadTimerRef.current && !shouldLoadVideo) {
            videoLoadTimerRef.current = setTimeout(() => {
              setShouldLoadVideo(true);
              videoLoadTimerRef.current = null;
            }, videoLoadDelay);
          }
        } else {
          clearVideoLoadTimer();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(card);
    return () => {
      observer.disconnect();
      clearVideoLoadTimer();
    };
  }, [cardRef, isVideo, shouldLoadVideo, videoLoadDelay, clearVideoLoadTimer]);

  // Release the video as soon as the card leaves view, so scrolling cannot
  // accumulate buffered videos holding decoder slots. No grace period is needed
  // to prevent thrashing: reloading requires `videoLoadDelay` of continuous
  // visibility, which already damps a card wobbling on the boundary. Only cards
  // with a poster are released — without one there would be nothing left to show.
  useEffect(() => {
    if (!isVideo || !poster) return;
    const card = cardRef.current;
    if (!card) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) return;
      setShouldLoadVideo(false);
      setVideoReady(false);
    });

    observer.observe(card);
    return () => observer.disconnect();
  }, [cardRef, isVideo, poster]);

  // Preload poster image
  useEffect(() => {
    if (!isVideo || !poster || mediaLoaded) return;
    const img = new Image();
    posterRef.current = img;
    img.onload = () => setMediaLoaded(true);
    img.onerror = () => {};
    img.src = poster;
    return () => {
      img.onload = null;
      img.onerror = null;
      posterRef.current = null;
    };
  }, [isVideo, poster, mediaLoaded]);

  // Fallback timeout
  useEffect(() => {
    if (mediaLoaded) return;
    const timeout = setTimeout(() => setMediaLoaded(true), 3000);
    return () => clearTimeout(timeout);
  }, [mediaLoaded, imageUrl]);

  const handleImageLoad = useCallback(() => setMediaLoaded(true), []);
  const setVideoReadyTrue = useCallback(() => setVideoReady(true), []);

  return {
    mediaLoaded,
    shouldLoadVideo,
    videoReady,
    handleImageLoad,
    setMediaLoaded,
    setVideoReadyTrue,
  };
};

export default useMediaLoading;
